import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { MtmStore } from '@/types';

export const useMtmStore = create<MtmStore>()(
  devtools(
    (set, get) => ({
      priceOverrides: new Map<number, string>(),
      excludedIds: new Set<number>(),
      repoExcludedIds: new Set<number>(),
      setOverride: (id, price) =>
        set((state) => ({
          priceOverrides: new Map(state.priceOverrides).set(id, price),
        })),
      clearOverride: (id) =>
        set((state) => {
          const next = new Map(state.priceOverrides);
          next.delete(id);
          return { priceOverrides: next };
        }),
      toggleExclude: (id) =>
        set((state) => {
          const next = new Set(state.excludedIds);
          if (next.has(id)) {
            next.delete(id);
          } else {
            next.add(id);
          }
          return { excludedIds: next };
        }),
      setExcluded: (ids) => set({ excludedIds: new Set(ids) }),
      excludeMany: (ids) =>
        set((state) => {
          if (ids.length === 0) return {};
          const next = new Set(state.excludedIds);
          for (const id of ids) next.add(id);
          return { excludedIds: next };
        }),
      includeMany: (ids) =>
        set((state) => {
          if (ids.length === 0) return {};
          const next = new Set(state.excludedIds);
          for (const id of ids) next.delete(id);
          // Same reference when nothing was actually excluded, so the memos keyed on
          // excludedIds (aggregate bar, exposure, footer) don't recompute for a no-op.
          return next.size === state.excludedIds.size ? {} : { excludedIds: next };
        }),
      toggleRepoExclude: (id) =>
        set((state) => {
          const next = new Set(state.repoExcludedIds);
          if (next.has(id)) {
            next.delete(id);
          } else {
            next.add(id);
          }
          return { repoExcludedIds: next };
        }),
      setRepoExcluded: (ids) => set({ repoExcludedIds: new Set(ids) }),
      reset: () =>
        set({
          priceOverrides: new Map(),
          excludedIds: new Set(),
          repoExcludedIds: new Set(),
        }),
      isOverridden: (id) => get().priceOverrides.has(id),
      isExcluded: (id) => get().excludedIds.has(id),
    }),
    { name: 'mtm-store', enabled: import.meta.env.DEV }
  )
);
